import { ProductCard, Seo, ShopLayout } from 'components'
import { Page } from 'core/components'
import { siteMetadata } from 'core/utils'
import { GetStaticProps } from 'next'
import { Brand, Category, Product } from 'model'
import { ReactElement } from 'react'
import { Dashboard, Search } from 'components/Shop'
import { BRAND_FIELDS, CATEGORY_FIELDS, PRODUCT_FIELDS, getSanityClient } from '@resellam/sanity'
import { useShop } from 'lib/shop'
import { fetch, filter, order, slice } from '@aonawale/sanity-query'
import { CLICK_EVENT_NAMES } from 'lib/algolia'
import { trackProducts } from 'lib/analytics'
import { HitsProps } from 'components/Shop/Hits'

const getBrands = () =>
  fetch<Brand[]>(
    getSanityClient(),
    {
      constraints: [
        filter('_type', '==', 'brand'),
        filter('featured', '==', true),
        order('_createdAt', 'asc'),
        slice(0, 4),
      ],
    },
    BRAND_FIELDS,
  )

const getCategories = () =>
  fetch<Category[]>(
    getSanityClient(),
    {
      constraints: [
        filter('_type', '==', 'category'),
        filter('active', '==', true),
        filter('featured', '==', true),
        order('_createdAt', 'asc'),
        slice(0, 4),
      ],
    },
    CATEGORY_FIELDS,
  )

const getProducts = () =>
  fetch<Product[]>(
    getSanityClient(),
    {
      constraints: [
        filter('_type', '==', 'product'),
        filter('active', '==', true),
        filter('featured', '==', true),
        filter('discount', '!=', null),
        filter('references(*[_type=="discount" && dateTime(endDate) >= dateTime(now())]._id)'),
        order('_createdAt', 'asc'),
        slice(0, 4),
      ],
    },
    PRODUCT_FIELDS,
  )

interface ShopPageProps {
  brands: Brand[],
  products: Product[],
  categories: Category[],
}

export const getStaticProps: GetStaticProps<ShopPageProps> = async () => ({
  props: {
    brands: await getBrands(),
    products: await getProducts(),
    categories: await getCategories(),
  },
})

const hits: HitsProps = {
  renderHit: ({ hit, sendEvent }) => (
    <ProductCard
      key={hit.objectID}
      href={`/shop/${hit.slug}`}
      product={{ ...hit, id: hit.objectID, variants: [] }}
      onClick={() => {
        // @ts-expect-error sendEvent doesn't make hit generic so conflicts with AlgoliaProductHit
        sendEvent('click', hit, CLICK_EVENT_NAMES.plpProductClicked)
        trackProducts('shop_view_product', {
          products: [
            {
              ...hit,
              variants: undefined,
              id: hit.objectID,
              variant: hit.variants?.join(', '),
              title: hit.title,
              brand: hit?.brand,
              category: hit?.category,
              price: hit?.price,
              condition: hit.condition,
              slug: hit.slug,
              image: hit.image,
            },
          ],
        })
      }}
    />
  ),
}

const filters = {
  brands: true,
  categories: true,
  condition: true,
  price: true,
}

const configure = { filters: 'price>0' }

const ShopPage = ({ categories, brands, products }: ShopPageProps) => {
  const { searching } = useShop()
  return (
    <Page title={searching ? undefined : 'Shop'}>
      <Seo
        title="Shop Phones, Laptops, Gadgets and more."
        description={`Buy phones, laptops, gadgets and more on ${siteMetadata.name} at affordable prices from the comfort of your home.`}
        canonical={`${siteMetadata.siteUrl}/shop`}
      />
      {searching ? (
        <Search
          sortBy
          configure={configure}
          filters={filters}
          hits={hits}
        />
      ) : (
        <Dashboard categories={categories} brands={brands} products={products} />
      )}
    </Page>
  )
}

ShopPage.getLayout = (page: ReactElement) => <ShopLayout>{page}</ShopLayout>

export default ShopPage
